/**
 * Scanner Audio — procedural dispatch center ambient sound.
 * Creates an immersive "police scanner" audio layer using Web Audio API.
 *
 * Features:
 *   - Radio static/crackle ambient (toggleable)
 *   - Alert tones for new CRITICAL/HIGH incidents
 *   - Dispatch beep between feed items
 *   - Roger beep after transmission ends
 *
 * All sounds are procedurally generated — no audio files needed.
 */

let ctx: AudioContext | null = null;
let staticNode: AudioBufferSourceNode | null = null;
let staticGain: GainNode | null = null;
let isPlaying = false;

function getContext(): AudioContext {
  if (!ctx) ctx = new AudioContext();
  if (ctx.state === 'suspended') ctx.resume();
  return ctx;
}

// ── Static/Crackle Generator ──────────────────────────────────────────

function createStaticBuffer(duration = 2): AudioBuffer {
  const c = getContext();
  const sr = c.sampleRate;
  const buf = c.createBuffer(1, sr * duration, sr);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i++) {
    // Mix of white noise + crackling pops
    const noise = (Math.random() * 2 - 1) * 0.03;
    const crackle = Math.random() > 0.998 ? (Math.random() - 0.5) * 0.4 : 0;
    data[i] = noise + crackle;
  }
  return buf;
}

export function startStatic(volume = 0.15) {
  if (isPlaying) return;
  const c = getContext();
  const buf = createStaticBuffer(3);

  staticGain = c.createGain();
  staticGain.gain.value = volume;
  staticGain.connect(c.destination);

  // Add a bandpass filter to make it sound more like radio static
  const filter = c.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.value = 2000;
  filter.Q.value = 0.5;
  filter.connect(staticGain);

  staticNode = c.createBufferSource();
  staticNode.buffer = buf;
  staticNode.loop = true;
  staticNode.connect(filter);
  staticNode.start();

  isPlaying = true;
}

export function stopStatic() {
  if (!isPlaying) return;
  try {
    staticNode?.stop();
    staticNode?.disconnect();
    staticGain?.disconnect();
  } catch {}
  staticNode = null;
  staticGain = null;
  isPlaying = false;
}

export function setStaticVolume(vol: number) {
  if (staticGain) staticGain.gain.value = Math.max(0, Math.min(1, vol));
}

export function isStaticPlaying(): boolean {
  return isPlaying;
}

// ── Alert Tones ───────────────────────────────────────────────────────

export function playAlertTone(level: 'CRITICAL' | 'HIGH' | 'MEDIUM' = 'HIGH') {
  const c = getContext();
  const osc = c.createOscillator();
  const gain = c.createGain();

  if (level === 'CRITICAL') {
    // Urgent dual-tone — like a real dispatch alert
    osc.type = 'square';
    osc.frequency.value = 880;
    gain.gain.setValueAtTime(0.2, c.currentTime);
    gain.gain.linearRampToValueAtTime(0, c.currentTime + 0.8);

    // Second tone
    const osc2 = c.createOscillator();
    osc2.type = 'square';
    osc2.frequency.value = 660;
    const gain2 = c.createGain();
    gain2.gain.setValueAtTime(0.15, c.currentTime + 0.1);
    gain2.gain.linearRampToValueAtTime(0, c.currentTime + 0.9);
    osc2.connect(gain2);
    gain2.connect(c.destination);
    osc2.start(c.currentTime + 0.1);
    osc2.stop(c.currentTime + 0.9);
  } else if (level === 'HIGH') {
    // Single rising tone
    osc.type = 'sine';
    osc.frequency.setValueAtTime(440, c.currentTime);
    osc.frequency.linearRampToValueAtTime(660, c.currentTime + 0.3);
    gain.gain.setValueAtTime(0.15, c.currentTime);
    gain.gain.linearRampToValueAtTime(0, c.currentTime + 0.5);
  } else {
    // Soft ping
    osc.type = 'sine';
    osc.frequency.value = 520;
    gain.gain.setValueAtTime(0.08, c.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, c.currentTime + 0.3);
  }

  osc.connect(gain);
  gain.connect(c.destination);
  osc.start();
  osc.stop(c.currentTime + 1);
}

// ── Dispatch Beep (between transmissions) ─────────────────────────────

export function playDispatchBeep() {
  const c = getContext();
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = 'sine';
  osc.frequency.value = 1000;
  gain.gain.setValueAtTime(0.1, c.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.001, c.currentTime + 0.12);
  osc.connect(gain);
  gain.connect(c.destination);
  osc.start();
  osc.stop(c.currentTime + 0.15);
}

// ── Roger Beep (end of transmission) ──────────────────────────────────

export function playRogerBeep() {
  const c = getContext();
  // Two quick descending tones
  for (let i = 0; i < 2; i++) {
    const osc = c.createOscillator();
    const gain = c.createGain();
    osc.type = 'sine';
    osc.frequency.value = i === 0 ? 1200 : 900;
    gain.gain.setValueAtTime(0.08, c.currentTime + i * 0.08);
    gain.gain.exponentialRampToValueAtTime(0.001, c.currentTime + i * 0.08 + 0.06);
    osc.connect(gain);
    gain.connect(c.destination);
    osc.start(c.currentTime + i * 0.08);
    osc.stop(c.currentTime + i * 0.08 + 0.08);
  }
}

// ── SOS Alarm ─────────────────────────────────────────────────────────

export function playSOSAlarm() {
  const c = getContext();
  // Wailing siren — oscillating frequency
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = 'sawtooth';
  osc.frequency.setValueAtTime(400, c.currentTime);
  // Oscillate between 400-800Hz over 2 seconds
  for (let i = 0; i < 4; i++) {
    osc.frequency.linearRampToValueAtTime(800, c.currentTime + 0.5 + i);
    osc.frequency.linearRampToValueAtTime(400, c.currentTime + 1 + i);
  }
  gain.gain.setValueAtTime(0.2, c.currentTime);
  gain.gain.linearRampToValueAtTime(0, c.currentTime + 4);
  osc.connect(gain);
  gain.connect(c.destination);
  osc.start();
  osc.stop(c.currentTime + 4);
}
